import { useMedplum } from "@medplum/react-hooks";
import { chatThreadQueries, resolveChatThreadTitle } from "@repo/agent-chat";
import {
  carePlanIdSchema,
  examIdSchema,
  locationIdSchema,
  patientIdSchema,
  questionnaireInstanceIdSchema,
  taskIdSchema,
} from "@repo/api/schemas";
import {
  useCarePlan,
  useCase,
  useDeviceDefinition,
  useDiagnosticDefinition,
  useExamAdministration,
  useExamDefinition,
  useLocation as useFhirLocation,
  useFindingDefinition,
  useFlowDefinition,
  usePatient,
  usePatientDocument,
  usePerformanceMeasure,
  useProgramDefinition,
  useProtocolDefinition,
  useQuestionnaireDefinition,
  useQuestionnaireInstance,
  useSchedule,
  useTasks,
  useTreatmentDefinition,
  useVisit,
} from "@repo/data-store";
import { asPerformanceMeasureId } from "@repo/domain";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import type { AssessmentKind } from "@/features/assessments/lib/assessment-detail-model";
import { deviceDefinitionUrlFromRouteId } from "@/features/catalog/lib/device-definition-route";
import { diagnosticDefinitionUrlFromRouteId } from "@/features/catalog/lib/diagnostic-definition-route";
import { examDefinitionUrlFromRouteId } from "@/features/catalog/lib/exam-definition-route";
import { findingDefinitionUrlFromRouteId } from "@/features/catalog/lib/finding-definition-route";
import { flowDefinitionUrlFromRouteId } from "@/features/catalog/lib/flow-definition-route";
import { programDefinitionUrlFromRouteId } from "@/features/catalog/lib/program-definition-route";
import { protocolDefinitionUrlFromRouteId } from "@/features/catalog/lib/protocol-definition-route";
import { questionnaireDefinitionUrlFromRouteId } from "@/features/catalog/lib/questionnaire-definition-route";
import { treatmentDefinitionUrlFromRouteId } from "@/features/catalog/lib/treatment-definition-route";
import { getActorInfo } from "@/features/scheduling";
import { getTaskTitle } from "@/features/tasks";
import { parseThreadIdParam } from "@/routes/chat/parse-thread-id-param";
import { AI_ENGINE_URL } from "@/shared/lib/ai-engine-url";
import { patientDisplayName } from "@/shared/lib/patient-utils";

const ASSESSMENT_KINDS: Readonly<Record<string, AssessmentKind>> = {
  q: "questionnaire",
  e: "exam",
  m: "performance-measure",
};

/** EpisodeOfCare status → breadcrumb label (a case has no name; label by state). */
const CASE_STATUS_LABEL: Record<string, string> = {
  active: "Active case",
  finished: "Closed case",
  cancelled: "Cancelled case",
  "entered-in-error": "Case",
};

export interface BreadcrumbData {
  pathnames: string[];
  patientId?: string;
  scheduleId?: string;
  visitId?: string;
  caseId?: string;
  carePlanId?: string;
  carePlanLabel: string | null;
  documentId?: string;
  /** The document's file name, or `null` until the collection has it. */
  documentTitle: string | null;
  /** The `q`/`e`/`m` discriminator segment under `/assessments`, which is not a page of its own. */
  assessmentKindSegment?: string;
  assessmentId?: string;
  assessmentTitle: string | null;
  taskId?: string;
  /** The raw `/chat/$threadId` path segment, so the renderer can spot that crumb. */
  chatThreadSegment?: string;
  /** Generated thread title, or `null` while it's still being generated. */
  chatThreadTitle: string | null;
  patientName: string | null;
  /** Whatever the `/catalog/<kind>/<id>` page shows, of any kind; `null` until loaded. */
  catalogTitle: string | null;
  scheduleActorInfo: ReturnType<typeof getActorInfo> | undefined;
  visitDate: string | null;
  caseName: string | null;
  taskBreadcrumb: string | null;
}

export function useBreadcrumbData(): BreadcrumbData {
  const location = useLocation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  const patientIndex = pathnames.indexOf("patients");
  const patientId = patientIndex !== -1 ? pathnames[patientIndex + 1] : undefined;

  // Catalog routes are `/catalog/<kind>/<id>`, so the kind segment says which
  // resource the id addresses — and `new` is a form, not an id.
  const catalogIndex = pathnames.indexOf("catalog");
  const catalogKind = catalogIndex !== -1 ? pathnames[catalogIndex + 1] : undefined;
  const catalogDetailSegment = catalogIndex !== -1 ? pathnames[catalogIndex + 2] : undefined;
  const catalogDetailId = catalogDetailSegment === "new" ? undefined : catalogDetailSegment;

  const catalogItemId =
    catalogKind === "procedures" ||
    catalogKind === "diagnostics" ||
    catalogKind === "protocols" ||
    catalogKind === "treatments" ||
    catalogKind === "exams" ||
    catalogKind === "devices"
      ? catalogDetailId
      : undefined;
  const questionnaireId = catalogKind === "questionnaires" ? catalogDetailId : undefined;

  const manageIndex = pathnames.indexOf("manage");
  const scheduleId =
    manageIndex !== -1 && pathnames[manageIndex - 1] === "schedule" ? pathnames[manageIndex + 1] : undefined;

  const visitsIndex = pathnames.indexOf("visits");
  const visitId = visitsIndex !== -1 && patientId ? pathnames[visitsIndex + 1] : undefined;

  const casesIndex = pathnames.indexOf("cases");
  const caseId = casesIndex !== -1 && patientId ? pathnames[casesIndex + 1] : undefined;

  const carePlansIndex = pathnames.indexOf("care-plans");
  const carePlanId = carePlansIndex !== -1 && patientId ? pathnames[carePlansIndex + 1] : undefined;

  const documentsIndex = pathnames.indexOf("documents");
  const documentId = documentsIndex !== -1 && patientId ? pathnames[documentsIndex + 1] : undefined;

  const assessmentsIndex = pathnames.indexOf("assessments");
  const assessmentKindSegment = assessmentsIndex !== -1 && patientId ? pathnames[assessmentsIndex + 1] : undefined;
  const assessmentKind = assessmentKindSegment ? ASSESSMENT_KINDS[assessmentKindSegment] : undefined;
  const assessmentId = assessmentKind ? pathnames[assessmentsIndex + 2] : undefined;

  // Only locations are addressed by a resource id now: `/catalog/devices/<id>`
  // is a device DEFINITION, and its units live on that page rather than on
  // routes of their own.
  const resourceId = catalogKind === "locations" ? catalogDetailId : undefined;

  const tasksIndex = pathnames.indexOf("tasks");
  const taskId = tasksIndex !== -1 ? pathnames[tasksIndex + 1] : undefined;

  // A chat thread is named by its generated title, never its id. Only the
  // `/chat/<id>` shape counts — the same parser the route uses, so a segment
  // the route would 404 on never triggers a thread lookup here.
  const chatIndex = pathnames.indexOf("chat");
  const chatThreadSegment = chatIndex !== -1 ? pathnames[chatIndex + 1] : undefined;
  const chatThreadId = chatThreadSegment ? parseThreadIdParam(chatThreadSegment) : null;

  const { patient } = usePatient(patientId ? patientIdSchema.parse(patientId) : undefined);
  // The breadcrumb only needs a title, and a catalog id addresses exactly one
  // kind — so ask each and take whichever answered.
  const programUrl = catalogKind === "procedures" ? programDefinitionUrlFromRouteId(catalogItemId) : undefined;
  const programDefinition = useProgramDefinition(programUrl);
  const diagnosticDefinition = useDiagnosticDefinition(diagnosticDefinitionUrlFromRouteId(catalogItemId));
  const treatmentDefinition = useTreatmentDefinition(treatmentDefinitionUrlFromRouteId(catalogItemId));
  const redesignedProtocol = useProtocolDefinition(protocolDefinitionUrlFromRouteId(catalogItemId));
  const examDefinitionUrl =
    pathnames.includes("exams") && catalogItemId ? examDefinitionUrlFromRouteId(catalogItemId) : "";
  const examDefinition = useExamDefinition(examDefinitionUrl);
  // Gated on the segment rather than asked unguarded: a device's canonical is
  // rebuilt from ITS base, so any other catalog id would resolve to a device
  // url that cannot exist.
  const deviceDefinition = useDeviceDefinition(
    catalogKind === "devices" ? deviceDefinitionUrlFromRouteId(catalogItemId) : undefined,
  );
  const questionnaire = useQuestionnaireDefinition(questionnaireDefinitionUrlFromRouteId(questionnaireId));
  const findingDefinition = useFindingDefinition(
    catalogKind === "findings" ? findingDefinitionUrlFromRouteId(catalogDetailId) : undefined,
  );
  const flowDefinition = useFlowDefinition(
    catalogKind === "flows" ? flowDefinitionUrlFromRouteId(catalogDetailId) : undefined,
  );
  const { schedule } = useSchedule(scheduleId);
  const { visit } = useVisit(visitId);
  const caseModel = useCase(caseId);
  // The editor and detail page share the same CarePlan identity.
  const carePlan = useCarePlan(carePlanId ? carePlanIdSchema.parse(carePlanId) : undefined);
  const { location: fhirLocation } = useFhirLocation(resourceId ? locationIdSchema.parse(resourceId) : undefined);
  const { tasks: taskData } = useTasks(taskId ? { taskId: taskIdSchema.parse(taskId) } : undefined);
  // Passed undefined off a document route, which keeps the breadcrumb from
  // consuming the collection: consuming it is what starts its subscription and
  // full fetch, and this hook runs on every page in the app.
  const patientDocument = usePatientDocument(documentId && patientId ? { patientId, documentId } : undefined);
  const assessmentInstance = useQuestionnaireInstance(
    assessmentKind === "questionnaire" && assessmentId ? questionnaireInstanceIdSchema.parse(assessmentId) : undefined,
  );
  const assessmentMeasure = usePerformanceMeasure(
    assessmentKind === "performance-measure" && assessmentId && patientId
      ? { measureId: asPerformanceMeasureId(assessmentId), patientId: patientIdSchema.parse(patientId) }
      : undefined,
  );
  const assessmentExam = useExamAdministration(
    assessmentKind === "exam" && assessmentId && patientId
      ? { examId: examIdSchema.parse(assessmentId), patientId: patientIdSchema.parse(patientId) }
      : undefined,
  );

  // Gated on actually being on a chat thread — the breadcrumb renders on every
  // route, and threads are nobody else's business. Shares the sessions rail's
  // query key, so on `/chat` this is already cached rather than a second fetch.
  const medplum = useMedplum();
  const { data: chatThreads } = useQuery({
    ...chatThreadQueries.list(medplum, AI_ENGINE_URL),
    enabled: chatThreadId !== null,
  });

  const scheduleActorInfo = schedule ? getActorInfo(schedule) : undefined;
  const patientName = patientDisplayName(patient) ?? null;
  const catalogItemTitle =
    redesignedProtocol?.name ??
    treatmentDefinition?.name ??
    diagnosticDefinition?.name ??
    examDefinition?.name ??
    programDefinition?.name ??
    deviceDefinition?.name ??
    null;
  const questionnaireTitle = questionnaire?.name ?? null;
  const visitDate = visit?.start ?? null;
  // A case has no name, so label it by lifecycle status (mirrors the care plan).
  const caseName = caseModel ? (CASE_STATUS_LABEL[caseModel.status] ?? "Case") : null;
  const carePlanLabel = carePlan ? (carePlan.status === "draft" ? "Draft plan" : "Active plan") : null;
  const resourceName = fhirLocation?.name ?? null;
  // Prefer the human task title (description / code) over the FHIR identifier
  // value so the breadcrumb reads as the task name, not an opaque id.
  const taskBreadcrumb = taskData[0] ? getTaskTitle(taskData[0]) : null;
  const chatThreadTitle = resolveChatThreadTitle(chatThreads, chatThreadId);
  const documentTitle = patientDocument?.title ?? null;
  const assessmentTitles = {
    questionnaire: assessmentInstance?.definition.name,
    exam: assessmentExam?.definitionName,
    "performance-measure": assessmentMeasure?.content.name,
  };
  const assessmentTitle = assessmentKind ? (assessmentTitles[assessmentKind] ?? null) : null;

  return {
    pathnames,
    patientId,
    scheduleId,
    visitId,
    caseId,
    carePlanId,
    documentId,
    documentTitle,
    taskId,
    assessmentKindSegment: assessmentId ? assessmentKindSegment : undefined,
    assessmentId,
    assessmentTitle,
    chatThreadSegment: chatThreadId === null ? undefined : chatThreadSegment,
    chatThreadTitle,
    patientName,
    carePlanLabel,
    catalogTitle:
      catalogItemTitle ??
      questionnaireTitle ??
      resourceName ??
      (findingDefinition?.name || null) ??
      flowDefinition?.name ??
      null,
    scheduleActorInfo,
    visitDate,
    caseName,
    taskBreadcrumb,
  };
}
